import axios from 'axios';
import { decamelizeKeys } from 'humps';
import type { ExtLink, ExtLinkEnv } from '@peloton/external-links/models';
import { toHref as toHrefDefault, toApiLink } from '@peloton/external-links/models';
import { toLocaleFromHostname } from '@peloton/internationalize/models/locale';
import { addAuthorizationHeaderInterceptor } from './authorizationHeaderInterceptor';
import {
  toDefaultRequestTransforms,
  toDefaultResponseTransforms,
} from './client-transforms';
import { ensureRequestHeaders } from './ensureRequestHeaders';
import type { Client, Platform } from './utils';

export const toClient = ({
  env,
  platform = 'web',
  toHref = toHrefDefault,
  headers = {},
  timeout,
  authorizationHeaderEnabled = false,
  acceptLanguageEnabled = false,
  withCredentials = true,
}: ClientOpts): Client => {
  const client = axios.create({
    baseURL: toHref(toApiLink('/'), env),
    responseType: 'json',
    withCredentials,
    timeout,
    headers: {
      'X-Requested-With': 'XmlHttpRequest',
      'Peloton-Platform': platform,
      ...headers,
    },
    transformRequest: toDefaultRequestTransforms(),
    transformResponse: toDefaultResponseTransforms(),
  });

  if (authorizationHeaderEnabled) {
    addAuthorizationHeaderInterceptor(client);
  }

  if (acceptLanguageEnabled) {
    client.interceptors.request.use(request => {
      const requestHeaders = ensureRequestHeaders(request);
      const currentAcceptLangHeader = requestHeaders['Accept-Language'];
      if (!currentAcceptLangHeader || currentAcceptLangHeader === 'undefined') {
        requestHeaders['Accept-Language'] = toLocaleFromHostname();
      }
      return request;
    });
  }

  return client;
};

type ClientOpts = {
  headers?: Object;
  env: ExtLinkEnv;
  platform?: Platform;
  timeout?: number;
  toHref?(link: ExtLink, env: ExtLinkEnv): string;
  authorizationHeaderEnabled?: boolean;
  acceptLanguageEnabled?: boolean;
  withCredentials?: boolean;
};

type CartCheckoutClientOpts = {
  baseURL: string;
  timeout?: number;
};

export const createCartCheckoutClient = ({
  baseURL,
  timeout = 60000,
}: CartCheckoutClientOpts): Client => {
  const client = axios.create({
    baseURL,
    responseType: 'json',
    timeout,
    transformRequest: toDefaultRequestTransforms(decamelizeKeys),
    transformResponse: toDefaultResponseTransforms(),
  });

  return client;
};
